import type { Localized } from '../i18n'

/**
 * Portfolio — the single place to add case studies.
 *
 * Media lives in /public/portfolio/<id>/ and is referenced with a root-relative path,
 * e.g. cover: 'portfolio/hotel-booking/cover.webp'. Every media field is optional:
 * without a cover the card renders a generated wireframe preview (see `coverKind`).
 * See README.md → "Adding a portfolio project" for the full checklist.
 */

export type PortfolioStatus = 'concept' | 'in-progress' | 'live'
export type CoverKind = 'site' | 'dashboard' | 'archive'
/** Grid footprint on desktop: s = 5/12, m = 6/12, l = 7/12, xl = full row. */
export type CardSize = 's' | 'm' | 'l' | 'xl'

export interface PortfolioVideo {
  /** `file` = self-hosted mp4/webm; `youtube`/`vimeo` accept a full URL or the bare id. */
  kind: 'file' | 'youtube' | 'vimeo'
  src: string
  poster?: string
  /** ISO 8601 duration, e.g. 'PT55S'; used in the VideoObject schema. */
  duration?: string
}

export interface PortfolioLink {
  kind: 'live' | 'case' | 'repo' | 'store'
  url: string
}

export interface PortfolioShot {
  src: string
  caption?: Localized
}

export interface PortfolioSeo {
  /** <title> of the case page (≈ 60 chars). Falls back to "<title> | Case study | CRAFT TOWER". */
  title?: Localized
  /** Meta description of the case page (≈ 155 chars). Falls back to `summary`. */
  description?: Localized
  keywords?: Localized<string[]>
  /** Social preview image (1200×630), root-relative like the other media. Falls back to `cover`. */
  image?: string
  /** ISO date the case went public; feeds sitemap <lastmod> and schema.org dates. */
  published?: string
  modified?: string
}

export interface PortfolioItem {
  /** URL slug: the case lives at /work/<id>/, /me/work/<id>/ and /ru/work/<id>/. */
  id: string
  status: PortfolioStatus
  year?: string
  client?: string
  size?: CardSize
  category: Localized
  title: Localized
  /** Short card text. Falls back to `description`. */
  summary?: Localized
  description: Localized
  outcome?: Localized
  /** Bullet list shown on the case page ("What we built"). */
  highlights?: Localized<string[]>
  seo?: PortfolioSeo
  tags: Localized<string[]>
  stack?: string[]
  cover?: string
  /** Wireframe illustration used when there is no `cover` image. */
  coverKind?: CoverKind
  /** Short muted loop played on card hover (mp4/webm, ≤ 3 MB recommended). */
  previewVideo?: string
  video?: PortfolioVideo
  gallery?: PortfolioShot[]
  links?: PortfolioLink[]
}

// Live products first, then the design concepts. A concept without `links` shows a "Soon" line on its card.
export const portfolio: PortfolioItem[] = [
  {
    id: 'montegigs',
    status: 'live',
    year: '2026',
    client: 'MonteGigs',
    size: 'xl',
    category: { en: 'Live music', me: 'Živa muzika', ru: 'Живая музыка' },
    title: {
      en: 'MonteGigs: live-music listings, redesigned',
      me: 'MonteGigs: redizajn vodiča za live muziku',
      ru: 'MonteGigs: редизайн афиши живой музыки',
    },
    summary: {
      en: "A dark, WebGL-driven redesign of Montenegro's live-music listings: one fixed 3D stage, one agenda, three languages.",
      me: 'Tamni WebGL redizajn crnogorskog vodiča za live muziku: jedna fiksna 3D scena, jedan program, tri jezika.',
      ru: 'Тёмный WebGL-редизайн афиши живой музыки Черногории: одна фиксированная 3D-сцена, одна афиша, три языка.',
    },
    description: {
      en: 'MonteGigs lists gigs, festivals, venues, artists and cities across Montenegro. We rebuilt the public site around a single fixed Three.js stage whose terrain, light and colour change with each section, so the page reads as one landscape while the listings scroll past it. The agenda filters by date, free entry, event type, genre and city, and every result is a shareable URL. Every event, venue, artist and city has its own path and a server-rendered page.',
      me: 'MonteGigs okuplja svirke, festivale, klubove, izvođače i gradove širom Crne Gore. Javni sajt smo ponovo izgradili oko jedne fiksne Three.js scene čiji se teren, svjetlo i boja mijenjaju sa svakom sekcijom, pa stranica djeluje kao jedan pejzaž dok se program pomjera ispred njega. Program se filtrira po datumu, slobodnom ulazu, vrsti događaja, žanru i gradu, a svaki rezultat je link koji se može podijeliti. Svaki događaj, klub, izvođač i grad ima sopstvenu adresu i stranicu renderovanu na serveru.',
      ru: 'MonteGigs собирает концерты, фестивали, площадки, артистов и города Черногории. Мы заново построили публичный сайт вокруг одной фиксированной сцены на Three.js: рельеф, свет и цвет меняются в каждом разделе, и страница читается как единый пейзаж, по которому проплывает афиша. Афиша фильтруется по дате, бесплатному входу, типу события, жанру и городу, а любой результат — ссылка, которой можно поделиться. У каждого события, площадки, артиста и города — свой адрес и страница, отрендеренная на сервере.',
    },
    outcome: {
      en: 'A distinctive, fast, fully trilingual listings site that is easy to share, easy to search and still works on weak devices.',
      me: 'Prepoznatljiv, brz i potpuno trojezičan sajt sa programom koji je lako dijeliti i pretraživati, a radi i na slabijim uređajima.',
      ru: 'Узнаваемый, быстрый и полностью трёхъязычный сайт афиши: им легко делиться, его легко находить, и он работает даже на слабых устройствах.',
    },
    highlights: {
      en: [
        'One WebGL stage behind every section, with a graceful fallback: weaker devices, low memory and Save-Data skip the 3D scene.',
        'Agenda filters that read like a question: date, free entry, event type, genre and city, with the state kept in the URL.',
        'A real URL for every event, festival, venue, artist and city, server-rendered at the edge for search and sharing.',
        'A festival guide deck with one slide per festival and a season timeline you can scrub.',
        'Two themes, Nocturne and Daylight, from one token set, plus a Halloween season behind a black cat.',
        'English, Russian and Montenegrin on real URLs, with Slavic plural rules and Intl dates and prices.',
      ],
      me: [
        'Jedna WebGL scena iza svake sekcije, uz uredan rezervni prikaz: slabiji uređaji, malo memorije i Save-Data preskaču 3D scenu.',
        'Filteri programa koji se čitaju kao pitanje: datum, slobodan ulaz, vrsta događaja, žanr i grad, a stanje ostaje u adresi.',
        'Prava adresa za svaki događaj, festival, klub, izvođača i grad, renderovana na ivici mreže za pretragu i dijeljenje.',
        'Vodič kroz festivale kao špil slajdova: po jedan slajd za festival i vremenska linija sezone koju možete pomjerati.',
        'Dvije teme, Nocturne i Daylight, iz jednog skupa tokena, plus Halloween sezona iza crne mačke.',
        'Engleski, ruski i crnogorski na pravim adresama, sa slovenskim pravilima množine i Intl formatima datuma i cijena.',
      ],
      ru: [
        'Одна WebGL-сцена за каждым разделом с мягким запасным вариантом: слабые устройства, малая память и Save-Data пропускают 3D.',
        'Фильтры афиши, которые читаются как вопрос: дата, бесплатный вход, тип события, жанр и город; состояние хранится в адресе.',
        'Настоящий URL у каждого события, фестиваля, площадки, артиста и города, серверный рендеринг на периферии для поиска и шеринга.',
        'Гид по фестивалям в виде колоды слайдов: слайд на фестиваль и сезонная шкала времени, которую можно перематывать.',
        'Две темы, Nocturne и Daylight, из одного набора токенов и хэллоуинский сезон за чёрным котом.',
        'Английский, русский и черногорский на реальных URL, славянские правила множественного числа и форматы дат и цен через Intl.',
      ],
    },
    tags: {
      en: ['WebGL', 'Three.js', 'Multilingual', 'Edge rendering'],
      me: ['WebGL', 'Three.js', 'Višejezičnost', 'Edge renderovanje'],
      ru: ['WebGL', 'Three.js', 'Мультиязычность', 'Edge-рендеринг'],
    },
    stack: ['Vite', 'Three.js', 'Lenis', 'Cloudflare Workers', 'D1', 'R2', 'Telegram bot'],
    cover: 'portfolio/montegigs/cover.webp',
    previewVideo: 'portfolio/montegigs/preview.mp4',
    video: { kind: 'file', src: 'portfolio/montegigs/presentation.mp4', poster: 'portfolio/montegigs/poster.webp', duration: 'PT55S' },
    gallery: [
      {
        src: 'portfolio/montegigs/01-agenda.webp',
        caption: { en: 'Agenda over the 3D stage', me: 'Program iznad 3D scene', ru: 'Афиша поверх 3D-сцены' },
      },
      {
        src: 'portfolio/montegigs/02-filters.webp',
        caption: { en: 'Filters: date, genre, city', me: 'Filteri: datum, žanr, grad', ru: 'Фильтры: дата, жанр, город' },
      },
      {
        src: 'portfolio/montegigs/03-festivals.webp',
        caption: { en: 'Festivals rail', me: 'Festivali', ru: 'Лента фестивалей' },
      },
      {
        src: 'portfolio/montegigs/04-event.webp',
        caption: { en: 'Event page with its own URL', me: 'Stranica događaja sa sopstvenom adresom', ru: 'Страница события со своим URL' },
      },
      {
        src: 'portfolio/montegigs/05-venue.webp',
        caption: { en: 'Venue profile', me: 'Profil kluba', ru: 'Профиль площадки' },
      },
      {
        src: 'portfolio/montegigs/06-guide.webp',
        caption: { en: 'Festival guide deck', me: 'Vodič kroz festivale', ru: 'Гид по фестивалям' },
      },
      {
        src: 'portfolio/montegigs/07-daylight.webp',
        caption: { en: 'Daylight theme', me: 'Daylight tema', ru: 'Светлая тема Daylight' },
      },
      {
        src: 'portfolio/montegigs/08-montenegrin.webp',
        caption: { en: 'Montenegrin interface', me: 'Crnogorski interfejs', ru: 'Черногорский интерфейс' },
      },
    ],
    links: [{ kind: 'live', url: 'https://montegigs.me' }],
    seo: {
      title: {
        en: 'MonteGigs redesign: WebGL live-music listings | CRAFT TOWER',
        me: 'MonteGigs redizajn: WebGL vodič za live muziku | CRAFT TOWER',
        ru: 'MonteGigs: WebGL-афиша живой музыки, кейс | CRAFT TOWER',
      },
      description: {
        en: 'How CRAFT TOWER redesigned MonteGigs, the live-music guide for Montenegro: a fixed Three.js stage, shareable agenda filters, a festival deck, three languages.',
        me: 'Kako je CRAFT TOWER redizajnirao MonteGigs, vodič za live muziku u Crnoj Gori: fiksna Three.js scena, filteri programa, vodič kroz festivale, tri jezika.',
        ru: 'Как CRAFT TOWER переосмыслил MonteGigs, афишу живой музыки Черногории: фиксированная Three.js-сцена, фильтры афиши, гид по фестивалям, три языка.',
      },
      keywords: {
        en: ['MonteGigs', 'live music Montenegro', 'concerts Montenegro', 'WebGL website', 'Three.js', 'web design case study'],
        me: ['MonteGigs', 'live muzika Crna Gora', 'koncerti Crna Gora', 'WebGL sajt', 'Three.js', 'studija slučaja web dizajn'],
        ru: ['MonteGigs', 'живая музыка Черногория', 'концерты Черногория', 'WebGL сайт', 'Three.js', 'кейс веб-дизайн'],
      },
      image: 'portfolio/montegigs/og.jpg',
      published: '2026-10-17',
      modified: '2026-10-23',
    },
  },
  {
    id: 'starfall-valley',
    status: 'live',
    year: '2026',
    size: 'm',
    category: { en: 'Education game', me: 'Edukativna igra', ru: 'Обучающая игра' },
    title: {
      en: 'Starfall Valley: times tables in a voxel valley',
      me: 'Starfall Valley: tablica množenja u voksel dolini',
      ru: 'Starfall Valley: таблица умножения в воксельной долине',
    },
    summary: {
      en: 'A browser game that teaches the times tables 1–10 in an anime voxel valley, with two voiced guides and no ads or tracking.',
      me: 'Igra u pregledaču koja uči tablicu množenja 1–10 u anime voksel dolini, sa dva glasovna vodiča, bez reklama i praćenja.',
      ru: 'Браузерная игра, которая учит таблице умножения 1–10 в аниме-воксельной долине: два озвученных наставника, без рекламы и трекинга.',
    },
    description: {
      en: "Starfall Valley teaches the multiplication tables 1–10 to children aged 10 and up. Two voxel guides lead the player: Aria, the valley's architect, and Mochi, her ginger cat. Every fact learned earns Star Dust, and Star Dust raises new buildings in a golden-hour valley. Lessons follow how memory works, with animated tricks, hints that teach, spaced repetition and a map of all 100 facts. It runs in the browser in English, Russian and Montenegrin, with no account, no ads and no tracking.",
      me: 'Starfall Valley uči tablicu množenja 1–10 djecu od 10 godina. Igrača vode dva voksel vodiča: Aria, arhitekta doline, i Mochi, njena riđa mačka. Svaka naučena činjenica donosi Zvjezdanu prašinu, a prašina podiže nove zgrade u dolini zlatnog sata. Lekcije prate način na koji pamćenje funkcioniše: animirani trikovi, savjeti koji uče, razmaknuto ponavljanje i mapa svih 100 činjenica. Radi u pregledaču na engleskom, ruskom i crnogorskom, bez naloga, reklama i praćenja.',
      ru: 'Starfall Valley учит таблице умножения 1–10 детей от 10 лет. Игрока ведут два воксельных наставника: Aria, архитектор долины, и Mochi, её рыжий кот. За каждый выученный факт игрок получает Звёздную пыль, а пыль возводит новые здания в долине золотого часа. Уроки следуют тому, как работает память: анимированные приёмы, подсказки, которые учат, интервальные повторения и карта всех 100 фактов. Игра работает в браузере на английском, русском и черногорском, без аккаунта, рекламы и трекинга.',
    },
    outcome: {
      en: 'A calm, fast learning game that kids start in seconds and parents can trust: nothing to install, nothing to sign up for, nothing to sell.',
      me: 'Mirna, brza igra za učenje koju djeca pokreću za nekoliko sekundi, a roditelji joj mogu vjerovati: ništa za instalaciju, registraciju ili prodaju.',
      ru: 'Спокойная и быстрая обучающая игра: ребёнок начинает за секунды, а родителям можно доверять — ничего не нужно устанавливать, ни регистрироваться, ни покупать.',
    },
    highlights: {
      en: [
        'Eight lessons ordered by difficulty, with animated blueprint tricks: a clock for the fives, a finger trick for the nines.',
        'Hints that teach, not tell: a wrong answer opens a strategy hint, and a second miss shows a worked example.',
        'Spaced repetition in five Leitner boxes and a Fact Map of all 100 facts, with a one-click drill for each twin pair.',
        'Star Dust pays for progress, not repetition, so rewards cannot be farmed; eight buildings rise block by block and 13 hidden friends wait in the valley.',
        'A procedurally generated voxel valley with golden-hour and twilight skies, a first-person Village Walk and 528 recorded voice lines in two languages.',
        'Vanilla JavaScript and Three.js on Cloudflare Workers: an installable PWA that works offline, touch-first at seven device sizes, with no tracking.',
      ],
      me: [
        'Osam lekcija poredanih po težini, sa animiranim trikovima: sat za petice, trik prstima za devetke.',
        'Savjeti koji uče, a ne govore: pogrešan odgovor otvara savjet o strategiji, a drugi promašaj prikazuje riješen primjer.',
        'Razmaknuto ponavljanje u pet Lajtnerovih kutija i Mapa činjenica svih 100 množenja, sa vježbom jednim klikom za svaki par blizanaca.',
        'Zvjezdana prašina plaća napredak, a ne ponavljanje, pa se nagrade ne mogu „farmovati“; osam zgrada niče blok po blok, a 13 skrivenih prijatelja čeka u dolini.',
        'Proceduralno generisana voksel dolina sa nebom zlatnog sata i sumraka, šetnjom kroz selo iz prvog lica i 528 snimljenih glasovnih replika na dva jezika.',
        'Čisti JavaScript i Three.js na Cloudflare Workers: PWA koja se instalira i radi offline, prilagođena dodiru na sedam veličina uređaja, bez praćenja.',
      ],
      ru: [
        'Восемь уроков по возрастанию сложности и анимированные приёмы: часы для пятёрок, пальцы для девяток.',
        'Подсказки, которые учат, а не выдают ответ: ошибка открывает стратегию, вторая ошибка показывает разобранный пример.',
        'Интервальные повторения в пяти коробках Лейтнера и Карта фактов всех 100 примеров с тренировкой в один клик для каждой пары-близнецов.',
        'Звёздная пыль платит за прогресс, а не за повторение, поэтому награды не нафармить; восемь зданий вырастают блок за блоком, а в долине прячутся 13 друзей.',
        'Процедурно созданная воксельная долина с небом золотого часа и сумерек, прогулка по деревне от первого лица и 528 записанных реплик на двух языках.',
        'Чистый JavaScript и Three.js на Cloudflare Workers: устанавливаемое PWA, работает офлайн, ориентировано на касания на семи размерах устройств, без трекинга.',
      ],
    },
    tags: {
      en: ['Education', 'Game design', 'WebGL', 'PWA'],
      me: ['Edukacija', 'Dizajn igara', 'WebGL', 'PWA'],
      ru: ['Образование', 'Геймдизайн', 'WebGL', 'PWA'],
    },
    stack: ['JavaScript', 'Three.js', 'Cloudflare Workers', 'PWA'],
    cover: 'portfolio/starfall-valley/cover.webp',
    previewVideo: 'portfolio/starfall-valley/preview.mp4',
    video: {
      kind: 'file',
      src: 'portfolio/starfall-valley/presentation.mp4',
      poster: 'portfolio/starfall-valley/poster.webp',
      duration: 'PT52S',
    },
    gallery: [
      {
        src: 'portfolio/starfall-valley/01-valley.webp',
        caption: { en: 'The valley hub at golden hour', me: 'Središte doline u zlatni sat', ru: 'Долина в золотой час' },
      },
      {
        src: 'portfolio/starfall-valley/02-lesson.webp',
        caption: { en: 'A lesson: the clock trick for the fives', me: 'Lekcija: trik sa satom za petice', ru: 'Урок: часы для пятёрок' },
      },
      {
        src: 'portfolio/starfall-valley/03-practice.webp',
        caption: { en: 'Practice with instant feedback', me: 'Vježba sa trenutnim povratnim odgovorom', ru: 'Практика с мгновенной обратной связью' },
      },
      {
        src: 'portfolio/starfall-valley/04-fact-map.webp',
        caption: { en: 'Fact Map of all 100 facts', me: 'Mapa svih 100 činjenica', ru: 'Карта всех 100 фактов' },
      },
      {
        src: 'portfolio/starfall-valley/05-sky-island.webp',
        caption: { en: 'Unlocking the Sky Island', me: 'Otključavanje Nebeskog ostrva', ru: 'Открытие Небесного острова' },
      },
      {
        src: 'portfolio/starfall-valley/06-twilight-walk.webp',
        caption: { en: 'First-person Village Walk at twilight', me: 'Šetnja kroz selo iz prvog lica u sumrak', ru: 'Прогулка по деревне в сумерках' },
      },
      {
        src: 'portfolio/starfall-valley/07-russian.webp',
        caption: { en: 'Russian interface', me: 'Ruski interfejs', ru: 'Русский интерфейс' },
      },
    ],
    links: [{ kind: 'live', url: 'https://starfall-valley.craft-tower.com/' }],
    seo: {
      title: {
        en: 'Starfall Valley: voxel times-tables game | CRAFT TOWER',
        me: 'Starfall Valley: igra tablice množenja | CRAFT TOWER',
        ru: 'Starfall Valley: игра-таблица умножения | CRAFT TOWER',
      },
      description: {
        en: 'How CRAFT TOWER built Starfall Valley, a browser game that teaches the times tables 1–10 in a voxel valley: spaced repetition, voiced guides, no ads or tracking.',
        me: 'Kako je CRAFT TOWER napravio Starfall Valley, igru u pregledaču koja uči tablicu množenja 1–10 u voksel dolini: razmaknuto ponavljanje, glasovni vodiči, bez reklama.',
        ru: 'Как CRAFT TOWER создал Starfall Valley — браузерную игру, которая учит таблице умножения 1–10 в воксельной долине: интервальные повторения, озвучка, без рекламы.',
      },
      keywords: {
        en: ['Starfall Valley', 'multiplication tables game', 'times tables 1-10', 'learn multiplication', 'educational browser game', 'Three.js game'],
        me: ['Starfall Valley', 'igra tablice množenja', 'tablica množenja 1-10', 'učenje množenja', 'edukativna igra u pregledaču', 'Three.js igra'],
        ru: ['Starfall Valley', 'игра таблица умножения', 'таблица умножения 1-10', 'учим умножение', 'обучающая браузерная игра', 'Three.js игра'],
      },
      image: 'portfolio/starfall-valley/og.jpg',
      published: '2026-10-27',
      modified: '2026-10-27',
    },
  },
  {
    id: 'mirna-guesthouse',
    status: 'concept',
    year: '2026',
    size: 'm',
    category: { en: 'Hospitality', me: 'Turizam', ru: 'Гостеприимство' },
    title: {
      en: 'Mirna: stone guesthouse on the Bay of Kotor',
      me: 'Mirna: kameni gostinjac u Bokokotorskom zalivu',
      ru: 'Mirna: каменный гостевой дом в Бококоторском заливе',
    },
    summary: {
      en: 'A booking site for a stone guesthouse, where the theme switch turns a real-time 3D bay from golden afternoon to moonlit night.',
      me: 'Sajt za rezervacije kamenog gostinjca, gdje promjena teme pretvara 3D zaliv u realnom vremenu iz zlatnog popodneva u mjesečevu noć.',
      ru: 'Сайт бронирования каменного гостевого дома: переключатель темы превращает 3D-залив в реальном времени из золотого дня в лунную ночь.',
    },
    description: {
      en: "Mirna is a design concept for a small, premium guesthouse: five rooms in a restored 1891 captain's house on the Bay of Kotor. Guests explore a live Three.js bay, turn each room around as a 3D diorama and send a booking request with a quote that updates as they choose. The hosts answer from a built-in host desk where requests arrive live and quick replies go out in the guest's own language. Everything works in English and Montenegrin. The house, people and reviews are fictional.",
      me: 'Mirna je dizajnerski koncept malog, premium gostinjca: pet soba u obnovljenoj kapetanskoj kući iz 1891. u Bokokotorskom zalivu. Gosti istražuju 3D zaliv u realnom vremenu (Three.js), okreću svaku sobu kao 3D dioramu i šalju zahtjev za rezervaciju sa ponudom koja se mijenja uz svaki izbor. Domaćini odgovaraju iz ugrađenog panela, gdje zahtjevi stižu uživo, a brzi odgovori idu na jeziku gosta. Sve radi na engleskom i crnogorskom. Kuća, ljudi i recenzije su izmišljeni.',
      ru: 'Mirna — дизайн-концепт небольшого премиального гостевого дома: пять номеров в восстановленном капитанском доме 1891 года в Бококоторском заливе. Гости изучают 3D-залив на Three.js в реальном времени, поворачивают каждый номер как 3D-диораму и отправляют запрос на бронирование с расчётом стоимости, который меняется с каждым выбором. Хозяева отвечают из встроенной панели: запросы приходят вживую, быстрые ответы уходят на языке гостя. Всё работает на английском и черногорском. Дом, люди и отзывы вымышлены.',
    },
    outcome: {
      en: 'Shows a hospitality site that feels like the place itself: a calm, bilingual booking flow that tells guests the price before they ask.',
      me: 'Pokazuje sajt za turizam koji djeluje kao samo mjesto: miran, dvojezičan tok rezervacije koji gostu otkriva cijenu prije nego što pita.',
      ru: 'Показывает сайт для гостеприимства, который ощущается как само место: спокойное двуязычное бронирование, где цена понятна ещё до вопроса.',
    },
    highlights: {
      en: [
        'A live Three.js bay with reflective water; the theme sets the time of day, from golden afternoon to a moonlit night with lit windows and fireflies.',
        'Five rooms as cut-away 3D dioramas you can turn around, with day and night lighting.',
        'A quote that follows every choice: season rates, a discount from 7 nights, city tax, an honest calendar and guests capped at the room capacity.',
        "A built-in host desk: requests land live, replies go out in the guest's own language, and confirmations are blocked when stays overlap.",
        'English and Montenegrin switched in place, with translations checked by the compiler and Slavic plural rules.',
        'A strict Content-Security-Policy, no third-party requests, lazy-loaded 3D and a CSS fallback when WebGL is missing.',
      ],
      me: [
        'Živ 3D zaliv (Three.js) sa vodom koja odbija sliku; tema određuje doba dana, od zlatnog popodneva do mjesečeve noći sa osvijetljenim prozorima i krijesnicama.',
        'Pet soba kao presječene 3D diorame koje možete okretati, sa dnevnim i noćnim osvjetljenjem.',
        'Ponuda koja prati svaki izbor: sezonske cijene, popust od 7 noćenja, boravišna taksa, pošten kalendar i broj gostiju ograničen kapacitetom sobe.',
        'Ugrađeni panel domaćina: zahtjevi stižu uživo, odgovori idu na jeziku gosta, a potvrda je blokirana kad se boravci preklapaju.',
        'Engleski i crnogorski se mijenjaju na mjestu, uz prevode koje provjerava kompajler i slovenska pravila množine.',
        'Stroga Content-Security-Policy, bez zahtjeva trećih strana, 3D koji se učitava na zahtjev i CSS rezerva bez WebGL-a.',
      ],
      ru: [
        'Живой 3D-залив на Three.js с отражающей водой; тема задаёт время суток — от золотого дня до лунной ночи с освещёнными окнами и светлячками.',
        'Пять номеров как 3D-диорамы в разрезе, которые можно поворачивать, с дневным и ночным светом.',
        'Расчёт, который следует за каждым выбором: сезонные тарифы, скидка от 7 ночей, туристический сбор, честный календарь и число гостей в пределах вместимости номера.',
        'Встроенная панель хозяев: запросы приходят вживую, ответы уходят на языке гостя, а подтверждение блокируется при пересечении броней.',
        'Английский и черногорский переключаются на месте, переводы проверяет компилятор, учтены славянские правила множественного числа.',
        'Строгая Content-Security-Policy, никаких сторонних запросов, 3D подгружается по требованию, а без WebGL — CSS-запасной вариант.',
      ],
    },
    tags: {
      en: ['Hospitality', 'Three.js', 'Booking flow', 'Multilingual'],
      me: ['Turizam', 'Three.js', 'Tok rezervacije', 'Višejezičnost'],
      ru: ['Гостеприимство', 'Three.js', 'Бронирование', 'Мультиязычность'],
    },
    stack: ['TypeScript', 'Vite', 'Three.js', 'Cloudflare Workers'],
    cover: 'portfolio/mirna-guesthouse/cover.webp',
    previewVideo: 'portfolio/mirna-guesthouse/preview.mp4',
    video: {
      kind: 'file',
      src: 'portfolio/mirna-guesthouse/presentation.mp4',
      poster: 'portfolio/mirna-guesthouse/poster.webp',
      duration: 'PT54S',
    },
    gallery: [
      {
        src: 'portfolio/mirna-guesthouse/01-hero-day.webp',
        caption: { en: 'Hero by day: the bay at golden afternoon', me: 'Početna po danu: zaliv u zlatno popodne', ru: 'Главная днём: залив в золотой полдень' },
      },
      {
        src: 'portfolio/mirna-guesthouse/02-rooms.webp',
        caption: { en: 'Rooms as 3D diorama cards', me: 'Sobe kao 3D diorama kartice', ru: 'Номера как карточки-диорамы' },
      },
      {
        src: 'portfolio/mirna-guesthouse/03-room.webp',
        caption: { en: 'A room by night, turned around', me: 'Soba noću, okrenuta', ru: 'Номер ночью, повёрнутый' },
      },
      {
        src: 'portfolio/mirna-guesthouse/04-quote.webp',
        caption: { en: 'Booking request with a live quote', me: 'Zahtjev za rezervaciju sa ponudom uživo', ru: 'Запрос на бронирование с живым расчётом' },
      },
      {
        src: 'portfolio/mirna-guesthouse/05-host-desk.webp',
        caption: { en: 'Host desk with live requests', me: 'Panel domaćina sa zahtjevima uživo', ru: 'Панель хозяев с запросами вживую' },
      },
      {
        src: 'portfolio/mirna-guesthouse/06-where-night.webp',
        caption: { en: 'Location, in the dark theme', me: 'Lokacija, u tamnoj temi', ru: 'Расположение в тёмной теме' },
      },
      {
        src: 'portfolio/mirna-guesthouse/07-montenegrin.webp',
        caption: { en: 'Montenegrin interface', me: 'Crnogorski interfejs', ru: 'Черногорский интерфейс' },
      },
    ],
    links: [{ kind: 'live', url: 'https://mirna-guesthouse.craft-tower.com/' }],
    seo: {
      title: {
        en: 'Mirna: 3D bay guesthouse booking concept | CRAFT TOWER',
        me: 'Mirna: koncept sajta za rezervaciju gostinjca | CRAFT TOWER',
        ru: 'Mirna: концепт сайта гостевого дома | CRAFT TOWER',
      },
      description: {
        en: 'Design concept by CRAFT TOWER for a stone guesthouse on the Bay of Kotor: a real-time 3D bay, rooms as dioramas, a live booking quote and a host desk.',
        me: 'Dizajnerski koncept CRAFT TOWER-a za kameni gostinjac u Bokokotorskom zalivu: 3D zaliv u realnom vremenu, sobe kao diorame, ponuda uživo i panel domaćina.',
        ru: 'Дизайн-концепт CRAFT TOWER для каменного гостевого дома в Бококоторском заливе: 3D-залив в реальном времени, номера-диорамы, живой расчёт и панель хозяев.',
      },
      keywords: {
        en: ['guesthouse booking website', 'hotel website design', 'Bay of Kotor guesthouse', 'Three.js website', 'booking flow', 'web design concept'],
        me: ['sajt za rezervaciju gostinjca', 'dizajn sajta za hotel', 'gostinjac Boka Kotorska', 'Three.js sajt', 'tok rezervacije', 'koncept web dizajna'],
        ru: ['сайт бронирования гостевого дома', 'дизайн сайта отеля', 'гостевой дом Бока-Которская', 'Three.js сайт', 'бронирование', 'веб-дизайн концепт'],
      },
      image: 'portfolio/mirna-guesthouse/og.jpg',
      published: '2026-10-28',
      modified: '2026-10-28',
    },
  },
  {
    id: 'kotva-dashboard',
    status: 'concept',
    year: '2026',
    size: 'm',
    category: { en: 'Operations', me: 'Operacije', ru: 'Операции' },
    title: {
      en: 'Kotva: operations console with a 3D field map',
      me: 'Kotva: operativna konzola sa 3D mapom terena',
      ru: 'Kotva: операционная консоль с 3D-картой выездов',
    },
    summary: {
      en: 'A calm operations console for a small service business: jobs, reminders, documents, follow-ups, a live 3D field map and an AI assistant that re-plans the day.',
      me: 'Mirna operativna konzola za mali uslužni biznis: poslovi, podsjetnici, dokumenta, praćenje klijenata, 3D mapa terena uživo i AI asistent koji preplanira dan.',
      ru: 'Спокойная операционная консоль для небольшого сервисного бизнеса: заказы, напоминания, документы, follow-up, живая 3D-карта выездов и AI-ассистент, который перепланирует день.',
    },
    description: {
      en: 'Kotva is a design concept for the internal panel a small service company runs on, replacing spreadsheets and chat groups with one place for jobs, reminders, documents, client follow-ups and an AI assistant. The fictional workspace is Bura Servis, an HVAC, plumbing and electrical maintenance company on the Montenegrin coast. The operations lead starts the day with a streamed brief, sees four crews on a 3D relief of the coast, and lets the assistant re-plan the afternoon when a job runs late. It works in English and Montenegrin, in light and dark; the AI is emulated in the browser, so the whole concept runs as a static site.',
      me: 'Kotva je dizajnerski koncept internog panela na kojem radi mala uslužna firma: umjesto tabela i grupa za prepisku, jedno mjesto za poslove, podsjetnike, dokumenta, praćenje klijenata i AI asistenta. Izmišljeni radni prostor je Bura Servis, firma za održavanje klima, vodovoda i elektroinstalacija na crnogorskom primorju. Voditeljka operacija dan počinje pregledom koji se ispisuje uživo, vidi četiri ekipe na 3D reljefu obale i prepušta asistentu da preplanira popodne kad posao kasni. Radi na engleskom i crnogorskom, u svijetloj i tamnoj temi; AI je emuliran u pregledaču, pa cijeli koncept radi kao statički sajt.',
      ru: 'Kotva — дизайн-концепт внутренней панели, на которой работает небольшая сервисная компания: вместо таблиц и чатов — одно место для заказов, напоминаний, документов, переписки с клиентами и AI-ассистента. Вымышленное рабочее пространство — Bura Servis, компания по обслуживанию кондиционеров, сантехники и электрики на побережье Черногории. Руководитель операций начинает день с утренней сводки, видит четыре бригады на 3D-рельефе побережья и поручает ассистенту перепланировать вторую половину дня, если заказ затягивается. Работает на английском и черногорском, в светлой и тёмной теме; ИИ эмулируется в браузере, поэтому вся концепция — статический сайт.',
    },
    outcome: {
      en: 'Shows how a custom internal tool can replace scattered spreadsheets and chats for a small team, with an assistant that acts instead of only answering.',
      me: 'Pokazuje kako namjenski interni alat može zamijeniti raštrkane tabele i prepiske malog tima, uz asistenta koji djeluje, a ne samo odgovara.',
      ru: 'Показывает, как собственный внутренний инструмент заменяет разрозненные таблицы и чаты небольшой команды — с ассистентом, который действует, а не только отвечает.',
    },
    highlights: {
      en: [
        'A morning brief composed from live data, and suggested actions that change real state: reschedule a job, email the client, log it and redraw the route.',
        'A job board with work orders you can drag between five columns, with an undo toast.',
        'Reminders typed in plain words ("Call Ivana Friday 3pm, urgent") and read live into client, day, time and priority.',
        'One inbox for every client thread, with replies drafted in a friendly, formal or brief tone, in Montenegrin or English.',
        'A live Three.js relief map of the coast with crew routes, a shader-driven assistant orb and a Ctrl K command palette.',
        'English and Montenegrin, light and dark, responsive down to a phone, accessible and free of third-party requests.',
      ],
      me: [
        'Jutarnji pregled sastavljen od podataka uživo i predložene radnje koje mijenjaju stvarno stanje: pomjeranje posla, poruka klijentu, evidencija i nova ruta na mapi.',
        'Tabla poslova sa radnim nalozima koje prevlačite kroz pet kolona, uz obavještenje za poništavanje.',
        'Podsjetnici ukucani običnim riječima („Pozovi Ivanu u petak u 15h, hitno“) koji se uživo čitaju kao klijent, dan, vrijeme i prioritet.',
        'Jedno sanduče za sve razgovore sa klijentima, sa odgovorima u prijateljskom, formalnom ili kratkom tonu, na crnogorskom ili engleskom.',
        'Živa Three.js reljefna mapa obale sa rutama ekipa, shader orb asistenta i paleta komandi na Ctrl K.',
        'Engleski i crnogorski, svijetla i tamna tema, prilagođeno telefonu, pristupačno i bez zahtjeva trećih strana.',
      ],
      ru: [
        'Утренняя сводка из живых данных и предлагаемые действия, меняющие реальное состояние: перенести заказ, написать клиенту, записать в журнал и перестроить маршрут.',
        'Доска заказов с рабочими нарядами, которые перетаскиваются между пятью колонками, с уведомлением об отмене.',
        'Напоминания обычными словами («Позвонить Ивани в пятницу в 15:00, срочно»), которые на лету разбираются на клиента, день, время и приоритет.',
        'Один ящик для всех переписок с клиентами, с черновиками ответов в дружелюбном, формальном или кратком тоне, на черногорском или английском.',
        'Живая рельефная карта побережья на Three.js с маршрутами бригад, шейдерный шар ассистента и палитра команд по Ctrl K.',
        'Английский и черногорский, светлая и тёмная темы, адаптивность до телефона, доступность и никаких сторонних запросов.',
      ],
    },
    tags: {
      en: ['Dashboard', 'AI assistant', '3D map', 'Internal tools'],
      me: ['Kontrolni panel', 'AI asistent', '3D mapa', 'Interni alati'],
      ru: ['Дашборд', 'AI-ассистент', '3D-карта', 'Внутренние инструменты'],
    },
    stack: ['TypeScript', 'Vite', 'Three.js', 'Cloudflare Workers'],
    cover: 'portfolio/kotva-dashboard/cover.webp',
    previewVideo: 'portfolio/kotva-dashboard/preview.mp4',
    video: {
      kind: 'file',
      src: 'portfolio/kotva-dashboard/presentation.mp4',
      poster: 'portfolio/kotva-dashboard/poster.webp',
      duration: 'PT52S',
    },
    gallery: [
      {
        src: 'portfolio/kotva-dashboard/01-overview.webp',
        caption: { en: 'Overview with the morning brief', me: 'Pregled sa jutarnjim izvještajem', ru: 'Обзор с утренней сводкой' },
      },
      {
        src: 'portfolio/kotva-dashboard/02-assistant.webp',
        caption: { en: 'The assistant re-plans the afternoon', me: 'Asistent preplanira popodne', ru: 'Ассистент перепланирует день' },
      },
      {
        src: 'portfolio/kotva-dashboard/03-jobs.webp',
        caption: { en: 'Job board with work orders', me: 'Tabla poslova sa radnim nalozima', ru: 'Доска заказов с нарядами' },
      },
      {
        src: 'portfolio/kotva-dashboard/04-reminders.webp',
        caption: { en: 'Reminders read from plain words', me: 'Podsjetnici iz običnih riječi', ru: 'Напоминания из обычных слов' },
      },
      {
        src: 'portfolio/kotva-dashboard/05-followups.webp',
        caption: { en: 'Follow-ups with a drafted reply', me: 'Praćenje klijenata sa pripremljenim odgovorom', ru: 'Follow-up с готовым черновиком ответа' },
      },
      {
        src: 'portfolio/kotva-dashboard/06-light.webp',
        caption: { en: 'Light theme with the 3D field map', me: 'Svijetla tema sa 3D mapom terena', ru: 'Светлая тема с 3D-картой' },
      },
      {
        src: 'portfolio/kotva-dashboard/07-montenegrin.webp',
        caption: { en: 'Montenegrin interface', me: 'Crnogorski interfejs', ru: 'Черногорский интерфейс' },
      },
    ],
    links: [{ kind: 'live', url: 'https://kotva-dashboard.craft-tower.com/' }],
    seo: {
      title: {
        en: 'Kotva: operations dashboard with AI concept | CRAFT TOWER',
        me: 'Kotva: koncept operativnog panela sa AI | CRAFT TOWER',
        ru: 'Kotva: концепт операционной панели с ИИ | CRAFT TOWER',
      },
      description: {
        en: 'Design concept by CRAFT TOWER for a service business console: jobs, reminders, documents, client follow-ups, a live 3D field map and an AI assistant that re-plans the day.',
        me: 'Dizajnerski koncept CRAFT TOWER-a za konzolu uslužnog biznisa: poslovi, podsjetnici, dokumenta, praćenje klijenata, 3D mapa terena i AI asistent koji preplanira dan.',
        ru: 'Дизайн-концепт CRAFT TOWER для консоли сервисного бизнеса: заказы, напоминания, документы, follow-up, живая 3D-карта выездов и AI-ассистент, перепланирующий день.',
      },
      keywords: {
        en: ['operations dashboard', 'field service software', 'AI assistant dashboard', 'internal tools', '3D map Three.js', 'admin panel design'],
        me: ['operativni panel', 'softver za terenske usluge', 'AI asistent panel', 'interni alati', '3D mapa Three.js', 'dizajn admin panela'],
        ru: ['операционный дашборд', 'софт для выездных услуг', 'дашборд с AI-ассистентом', 'внутренние инструменты', '3D-карта Three.js', 'дизайн админ-панели'],
      },
      image: 'portfolio/kotva-dashboard/og.jpg',
      published: '2026-10-28',
      modified: '2026-10-28',
    },
  },
  {
    id: 'loza-family-archive',
    status: 'concept',
    year: '2026',
    size: 'm',
    category: { en: 'Private clients', me: 'Privatni klijenti', ru: 'Частные клиенты' },
    title: {
      en: 'Loza: a private family archive you travel through',
      me: 'Loza: privatni porodični arhiv kroz koji putujete',
      ru: 'Loza: приватный семейный архив, по которому путешествуешь',
    },
    summary: {
      en: 'A private family archive where scrolling travels back through a 3D tree of eight generations, with on-device search and cited AI answers.',
      me: 'Privatni porodični arhiv u kojem listanje putuje unazad kroz 3D stablo od osam generacija, sa pretragom na uređaju i AI odgovorima sa izvorima.',
      ru: 'Приватный семейный архив, где прокрутка страницы — путешествие назад по 3D-древу из восьми поколений, с поиском на устройстве и ответами ИИ со ссылками на источники.',
    },
    description: {
      en: "Loza is a design concept for a secure web app that keeps a family's documents, photographs, recorded voices, genealogy notes and search index readable for generations. Loza means vine or lineage in Montenegrin. The concept page is one continuous camera move through a Three.js root system of a fictional family: the present sits at the top, every generation back is a wider ring further down, and a year rail counts the journey. Search folds diacritics and Cyrillic on the device, the family tree shows how well each ancestor is backed by evidence, and the AI answers with citations. It works in English and Montenegrin, in a dark and a light theme.",
      me: 'Loza je dizajnerski koncept sigurne veb aplikacije koja čuva porodična dokumenta, fotografije, snimljene glasove, genealoške bilješke i indeks pretrage čitljivim generacijama. Loza znači vinova loza, rod. Stranica koncepta je jedno neprekidno kretanje kamere kroz Three.js korijenski sistem izmišljene porodice: sadašnjost je na vrhu, svaka generacija unazad je širi prsten niže, a traka godina broji put. Pretraga na uređaju ne pravi razliku među dijakritičkim znacima i ćirilicom, porodično stablo pokazuje koliko je svaki predak potkrijepljen dokazima, a AI odgovara sa navodima izvora. Radi na engleskom i crnogorskom, u tamnoj i svijetloj temi.',
      ru: 'Loza — дизайн-концепт защищённого веб-приложения, которое хранит документы, фотографии, записанные голоса, генеалогические заметки и поисковый индекс семьи так, чтобы их можно было прочитать через поколения. На черногорском «loza» значит «лоза, род». Страница концепции — одно непрерывное движение камеры сквозь корневую систему на Three.js вымышленной семьи: настоящее наверху, каждое поколение назад — всё более широкое кольцо ниже, а шкала лет отсчитывает путь. Поиск на устройстве игнорирует диакритику и понимает кириллицу, дерево показывает, насколько подтверждён каждый предок, а ИИ отвечает со ссылками на источники. Работает на английском и черногорском, в тёмной и светлой теме.',
    },
    outcome: {
      en: 'Shows that our work goes beyond generic websites into tailored digital products that explain their own privacy and longevity.',
      me: 'Pokazuje da naš rad prevazilazi generičke sajtove i vodi do prilagođenih digitalnih proizvoda koji sami objašnjavaju privatnost i dugovječnost.',
      ru: 'Показывает, что наша работа выходит за рамки шаблонных сайтов — к индивидуальным цифровым продуктам, которые сами объясняют свою приватность и долговечность.',
    },
    highlights: {
      en: [
        'Scrolling is time travel: each section owns a camera position and a year, from 2026 at the top down to 1824 and out to 2126.',
        'A 129-person family tree in WebGL with four evidence levels (confirmed, probable, family lore, unknown) and hover cards with sources.',
        'On-device search that forgives spelling: "djurovic" finds Đurović and Cyrillic "Цетиње" finds Cetinje, with zero network requests.',
        '"Ask Loza" answers stream with numbered citations and flag hard-to-read words for review instead of guessing.',
        'Security and longevity explained visually: 2-of-3 recovery keys, open formats, one-click export and a timeline to 2126.',
        'No cookies, analytics or third-party requests, a strict CSP, and two languages and two themes from one token set.',
      ],
      me: [
        'Listanje je putovanje kroz vrijeme: svaka sekcija ima poziciju kamere i godinu, od 2026. na vrhu do 1824. i dalje do 2126.',
        'Porodično stablo od 129 osoba u WebGL-u sa četiri nivoa dokaza (potvrđeno, vjerovatno, porodična predanja, nepoznato) i karticama sa izvorima.',
        'Pretraga na uređaju koja prašta pravopis: „djurovic“ nalazi Đurović, a ćirilično „Цетиње“ nalazi Cetinje, bez ijednog mrežnog zahtjeva.',
        'Odgovori „Pitaj Lozu“ stižu riječ po riječ sa numerisanim izvorima i označavaju teško čitljive riječi za provjeru umjesto da pogađaju.',
        'Sigurnost i dugovječnost objašnjene slikom: ključevi za oporavak 2 od 3, otvoreni formati, izvoz jednim klikom i vremenska linija do 2126.',
        'Bez kolačića, analitike i zahtjeva trećih strana, stroga CSP, te dva jezika i dvije teme iz jednog skupa tokena.',
      ],
      ru: [
        'Прокрутка — путешествие во времени: у каждого раздела своя позиция камеры и год, от 2026 наверху вниз к 1824 и дальше к 2126.',
        'Семейное древо из 129 человек в WebGL с четырьмя уровнями доказательности (подтверждено, вероятно, семейное предание, неизвестно) и карточками с источниками.',
        'Поиск на устройстве, который прощает написание: «djurovic» находит Đurović, а кириллическое «Цетиње» находит Cetinje — без единого сетевого запроса.',
        'Ответы «Спросить Лозу» появляются по слову с нумерованными ссылками и помечают трудно читаемые слова на проверку, а не угадывают.',
        'Безопасность и долговечность наглядно: ключи восстановления 2 из 3, открытые форматы, экспорт в один клик и шкала времени до 2126.',
        'Никаких cookies, аналитики и сторонних запросов, строгая CSP, два языка и две темы из одного набора токенов.',
      ],
    },
    tags: {
      en: ['Private app', 'Scroll storytelling', 'Search', 'Privacy by design'],
      me: ['Privatna aplikacija', 'Priča kroz skrol', 'Pretraga', 'Privatnost po dizajnu'],
      ru: ['Приватное приложение', 'Сторителлинг при прокрутке', 'Поиск', 'Приватность по умолчанию'],
    },
    stack: ['TypeScript', 'Vite', 'Three.js', 'Cloudflare Workers'],
    cover: 'portfolio/loza-family-archive/cover.webp',
    previewVideo: 'portfolio/loza-family-archive/preview.mp4',
    video: {
      kind: 'file',
      src: 'portfolio/loza-family-archive/presentation.mp4',
      poster: 'portfolio/loza-family-archive/poster.webp',
      duration: 'PT54S',
    },
    gallery: [
      {
        src: 'portfolio/loza-family-archive/01-archive.webp',
        caption: { en: 'The archive app', me: 'Aplikacija arhiva', ru: 'Приложение архива' },
      },
      {
        src: 'portfolio/loza-family-archive/02-tree.webp',
        caption: { en: 'A family tree that shows its evidence', me: 'Porodično stablo koje pokazuje dokaze', ru: 'Семейное древо с доказательствами' },
      },
      {
        src: 'portfolio/loza-family-archive/03-search.webp',
        caption: { en: 'Search that folds diacritics', me: 'Pretraga koja ne pravi razliku među dijakritičkim znacima', ru: 'Поиск без учёта диакритики' },
      },
      {
        src: 'portfolio/loza-family-archive/04-ask-loza.webp',
        caption: { en: 'Ask Loza, with citations', me: 'Pitaj Lozu, sa izvorima', ru: 'Спросить Лозу, со ссылками' },
      },
      {
        src: 'portfolio/loza-family-archive/05-security.webp',
        caption: { en: 'Security: 2-of-3 recovery', me: 'Sigurnost: oporavak 2 od 3', ru: 'Безопасность: восстановление 2 из 3' },
      },
      {
        src: 'portfolio/loza-family-archive/06-light.webp',
        caption: { en: 'Paper & Ink light theme', me: 'Svijetla tema Paper & Ink', ru: 'Светлая тема Paper & Ink' },
      },
      {
        src: 'portfolio/loza-family-archive/07-montenegrin.webp',
        caption: { en: 'Montenegrin interface', me: 'Crnogorski interfejs', ru: 'Черногорский интерфейс' },
      },
    ],
    links: [{ kind: 'live', url: 'https://loza-archive.craft-tower.com/' }],
    seo: {
      title: {
        en: 'Loza: private family archive concept | CRAFT TOWER',
        me: 'Loza: koncept privatnog porodičnog arhiva | CRAFT TOWER',
        ru: 'Loza: концепт приватного семейного архива | CRAFT TOWER',
      },
      description: {
        en: 'Design concept by CRAFT TOWER for a private family archive: scrolling travels through a 3D tree of eight generations, with on-device search and cited AI answers.',
        me: 'Dizajnerski koncept CRAFT TOWER-a za privatni porodični arhiv: listanje putuje kroz 3D stablo od osam generacija, uz pretragu na uređaju i AI odgovore sa izvorima.',
        ru: 'Дизайн-концепт CRAFT TOWER для приватного семейного архива: прокрутка идёт по 3D-древу из восьми поколений, есть поиск на устройстве и ответы ИИ со ссылками.',
      },
      keywords: {
        en: ['family archive app', 'genealogy web app', 'private archive', 'scroll storytelling', 'Three.js website', 'privacy by design'],
        me: ['aplikacija porodičnog arhiva', 'genealoška veb aplikacija', 'privatni arhiv', 'priča kroz skrol', 'Three.js sajt', 'privatnost po dizajnu'],
        ru: ['приложение семейного архива', 'генеалогическое веб-приложение', 'приватный архив', 'сторителлинг при прокрутке', 'Three.js сайт', 'приватность по умолчанию'],
      },
      image: 'portfolio/loza-family-archive/og.jpg',
      published: '2026-10-28',
      modified: '2026-10-28',
    },
  },

  // ── Template for a real project ─────────────────────────────────────────────
  // {
  //   id: 'client-project',
  //   status: 'live',
  //   year: '2026',
  //   client: 'Client name',
  //   size: 'l',
  //   category: { en: '…', me: '…', ru: '…' },
  //   title: { en: '…', me: '…', ru: '…' },
  //   summary: { en: '…', me: '…', ru: '…' },
  //   description: { en: '…', me: '…', ru: '…' },
  //   outcome: { en: '…', me: '…', ru: '…' },
  //   tags: { en: ['…'], me: ['…'], ru: ['…'] },
  //   stack: ['Next.js', 'Cloudflare', 'Three.js'],
  //   cover: 'portfolio/client-project/cover.webp',
  //   previewVideo: 'portfolio/client-project/preview.mp4',
  //   video: { kind: 'youtube', src: 'https://youtu.be/XXXXXXXXXXX' },
  //   gallery: [{ src: 'portfolio/client-project/01.webp', caption: { en: '…', me: '…', ru: '…' } }],
  //   links: [{ kind: 'live', url: 'https://example.com' }],
  // },
]
